import React, { useMemo } from 'react';
import {
  Check,
  CircleDot,
  Flag,
  Footprints,
  GitBranch,
  Loader2,
  MapPin,
  Navigation,
  Plus,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatDistance } from './hexGridMath';

const panelCopy = {
  fr: {
    title: 'Itinéraire', description: 'Un choix à présenter aux joueurs', transport: 'Transport', pace: 'Allure', danger: 'Danger régional',
    start: 'Départ', destination: 'Arrivée', chooseStart: 'Pointer le départ', chooseDestination: "Pointer l’arrivée",
    change: 'Modifier', waypoint: 'Passage obligé', addWaypoint: 'Ajouter un passage', remove: 'Retirer', reset: 'Nouveau trajet',
    instruction: 'Choisissez un départ et une arrivée sur la carte pour comparer les options.', alternatives: 'Options proposées',
    duration: 'Durée', distance: 'Distance', risk: 'Danger', exposure: 'Exposition', unknown: 'inconnus', unavailable: 'Aucun passage possible',
    select: 'Choisir', chosen: 'Choisi', qualify: 'Terrain à préciser', qualifyHelp: 'Cette zone est sur la route choisie. Indiquez son terrain pour fiabiliser le calcul.',
    remaining: 'restants', confirm: 'Valider pour la partie', confirmed: 'Trajet prêt pour la partie', confirmedHelp: 'Ce tracé devient la base du futur journal et peut encore être modifié.',
    saved: 'Enregistré', saving: 'Enregistrement…', unsaved: 'Modifié', stale: 'La grille a changé : choisissez de nouveau les points du trajet.',
    noScale: "Définissez d’abord l’échelle de la carte dans l’onglet Calage.",
    editRoute: 'Forcer un détour', editRouteHelp: 'Cliquez un hexagone du tracé pour l’exclure. Le chemin se recalcule autour sans perdre les autres réglages.',
    avoided: 'Détour autour de',
  },
  en: {
    title: 'Route', description: 'A decision to put before the players', transport: 'Transport', pace: 'Pace', danger: 'Regional danger',
    start: 'Start', destination: 'Destination', chooseStart: 'Pick start', chooseDestination: 'Pick destination',
    change: 'Change', waypoint: 'Required waypoint', addWaypoint: 'Add waypoint', remove: 'Remove', reset: 'New route',
    instruction: 'Pick a start and destination on the map to compare options.', alternatives: 'Suggested options',
    duration: 'Duration', distance: 'Distance', risk: 'Danger', exposure: 'Exposure', unknown: 'unknown', unavailable: 'No traversable route',
    select: 'Choose', chosen: 'Chosen', qualify: 'Terrain needs clarification', qualifyHelp: 'This area lies on the chosen route. Set its terrain to improve the estimate.',
    remaining: 'remaining', confirm: 'Confirm for play', confirmed: 'Route ready for play', confirmedHelp: 'This path becomes the basis of the future log and can still be changed.',
    saved: 'Saved', saving: 'Saving…', unsaved: 'Changed', stale: 'The grid changed: pick the route points again.',
    noScale: 'Set the map scale in the Alignment tab first.',
    editRoute: 'Force a detour', editRouteHelp: 'Click a route hex to exclude it. The path recalculates around it without losing other settings.',
    avoided: 'Detour around',
  },
};

const localLabel = (item, language) => item?.label?.[language] || item?.label?.fr || item?.id || '—';

const formatDuration = (hours, time, language) => {
  if (!Number.isFinite(hours)) return '—';
  const unit = time?.units?.find((candidate) => candidate.id === time.defaultJournalUnit);
  if (!unit) return `${hours.toLocaleString(language, { maximumFractionDigits: 1 })} h`;
  const value = hours * 3600 / unit.seconds;
  return `${value.toLocaleString(language, { maximumFractionDigits: value < 10 ? 1 : 0 })} ${localLabel(unit, language)}`;
};

export function TravelRoutePanel({
  language = 'fr',
  distanceUnit = 'km',
  time,
  transports = [],
  paces = [],
  dangerLevels = [],
  profiles = [],
  terrains = [],
  draft,
  onDraftChange,
  interactionMode,
  onInteractionModeChange,
  startLabel,
  destinationLabel,
  waypointLabels = [],
  onRemoveWaypoint,
  avoidedLabels = [],
  onRemoveAvoidedHex,
  variants = [],
  onReset,
  unknownHexLabel,
  unknownRemaining = 0,
  onAssignUnknownTerrain,
  onConfirm,
  saveStatus = 'saved',
  stale = false,
  scaleReady = false,
  profileColors = {},
}) {
  const text = panelCopy[language] || panelCopy.fr;
  const profileById = useMemo(() => Object.fromEntries(profiles.map((profile) => [profile.id, profile])), [profiles]);
  const selectedVariant = variants.find((variant) => variant.profileId === draft.selectedProfileId);
  const status = {
    saved: { label: text.saved, icon: Check, className: 'text-emerald-600 dark:text-emerald-300' },
    saving: { label: text.saving, icon: Loader2, className: 'text-amber-600 dark:text-amber-300' },
    dirty: { label: text.unsaved, icon: CircleDot, className: 'text-muted-foreground' },
  }[saveStatus];
  const StatusIcon = status.icon;
  const setOption = (key, value) => onDraftChange({
    ...draft,
    [key]: value,
    selectedProfileId: key === 'selectedProfileId' ? value : null,
    status: key === 'selectedProfileId' ? 'ready' : 'planning',
    variants: key === 'selectedProfileId' ? draft.variants : [],
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Navigation className="h-4 w-4" />{text.title}</CardTitle>
            <CardDescription>{text.description}</CardDescription>
          </div>
          <Badge variant="outline" className={status.className}>
            <StatusIcon className={`mr-1 h-3 w-3 ${saveStatus === 'saving' ? 'animate-spin' : ''}`} />{status.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {stale && <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200">{text.stale}</p>}

        <div className="space-y-2">
          <Label>{text.transport}</Label>
          <Select value={draft.transportId} onValueChange={(value) => setOption('transportId', value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{transports.map((item) => <SelectItem key={item.id} value={item.id}>{localLabel(item, language)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <Label>{text.pace}</Label>
            <Select value={draft.paceId} onValueChange={(value) => setOption('paceId', value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{paces.map((item) => <SelectItem key={item.id} value={item.id}>{localLabel(item, language)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{text.danger}</Label>
            <Select value={draft.dangerLevelId} onValueChange={(value) => setOption('dangerLevelId', value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{dangerLevels.map((item) => <SelectItem key={item.id} value={item.id}>{localLabel(item, language)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            ['route_start', text.start, startLabel, text.chooseStart, MapPin],
            ['route_destination', text.destination, destinationLabel, text.chooseDestination, Flag],
          ].map(([mode, label, value, emptyLabel, Icon]) => (
            <button
              key={mode}
              type="button"
              disabled={!scaleReady}
              onClick={() => onInteractionModeChange(interactionMode === mode ? 'navigate' : mode)}
              className={`rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${interactionMode === mode ? 'border-primary bg-primary/10' : 'hover:bg-muted/50'}`}
            >
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"><Icon className="h-3 w-3" />{label}</span>
              <span className="mt-1 block text-sm font-semibold">{value || emptyLabel}</span>
            </button>
          ))}
        </div>

        <div className="space-y-2">
          <Button variant={interactionMode === 'route_waypoint' ? 'secondary' : 'outline'} className="w-full" disabled={!scaleReady || !draft.startHexKey || !draft.destinationHexKey} onClick={() => onInteractionModeChange(interactionMode === 'route_waypoint' ? 'navigate' : 'route_waypoint')}>
            <Plus />{text.addWaypoint}
          </Button>
          {waypointLabels.map((label, index) => (
            <div key={`${label}-${index}`} className="flex items-center gap-2 rounded-md bg-muted/30 px-2 py-1.5 text-xs">
              <span className="flex-1"><strong>{text.waypoint} {index + 1}</strong> · {label}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" title={text.remove} onClick={() => onRemoveWaypoint(index)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Button
            variant={interactionMode === 'route_edit' ? 'secondary' : 'outline'}
            className="w-full"
            disabled={!selectedVariant || selectedVariant.unavailable}
            onClick={() => onInteractionModeChange(interactionMode === 'route_edit' ? 'navigate' : 'route_edit')}
          >
            <GitBranch />{text.editRoute}
          </Button>
          {interactionMode === 'route_edit' && <p className="text-xs leading-relaxed text-muted-foreground">{text.editRouteHelp}</p>}
          {avoidedLabels.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2 rounded-md border border-rose-500/20 bg-rose-500/5 px-2 py-1.5 text-xs">
              <span className="flex-1"><strong>{text.avoided}</strong> · {label}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" title={text.remove} onClick={() => onRemoveAvoidedHex(key)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>

        {!draft.startHexKey || !draft.destinationHexKey ? (
          <p className="rounded-lg border border-dashed p-3 text-xs leading-relaxed text-muted-foreground">{scaleReady ? text.instruction : text.noScale}</p>
        ) : (
          <div className="space-y-2">
            <Label>{text.alternatives}</Label>
            {variants.map((variant) => {
              const profile = profileById[variant.profileId];
              const selected = draft.selectedProfileId === variant.profileId;
              return (
                <button
                  type="button"
                  key={variant.profileId}
                  disabled={variant.unavailable}
                  onClick={() => setOption('selectedProfileId', variant.profileId)}
                  className={`w-full rounded-lg border p-3 text-left transition-all disabled:cursor-not-allowed disabled:opacity-55 ${selected ? 'border-current bg-muted/45 shadow-sm' : 'hover:bg-muted/30'}`}
                  style={selected ? { color: profileColors[variant.profileId] || 'currentColor' } : undefined}
                >
                  <span className="flex items-center justify-between gap-2">
                    <strong className="text-sm text-foreground">{localLabel(profile, language)}</strong>
                    <Badge variant={selected ? 'default' : 'outline'}>{selected ? text.chosen : text.select}</Badge>
                  </span>
                  {variant.unavailable ? <span className="mt-2 block text-xs text-muted-foreground">{text.unavailable}</span> : (
                    <>
                      <span className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span>{text.distance} <b className="text-foreground">{formatDistance(variant.distanceMeters, distanceUnit, language)}</b></span>
                        <span>{text.duration} <b className="text-foreground">{formatDuration(variant.durationHours, time, language)}</b></span>
                        <span>{text.risk} <b className="text-foreground">{variant.dangerIndex}/100</b></span>
                        <span>{text.exposure} <b className="text-foreground">{variant.exposureIndex}/100</b></span>
                      </span>
                      {variant.unknownHexKeys.length > 0 && <Badge variant="outline" className="mt-2 text-amber-700 dark:text-amber-300">{variant.unknownHexKeys.length} {text.unknown}</Badge>}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {selectedVariant && !selectedVariant.unavailable && unknownHexLabel && (
          <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">{text.qualify} · {unknownHexLabel}</p>
              <p className="mt-1 text-xs text-muted-foreground">{text.qualifyHelp}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{unknownRemaining} {text.remaining}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {terrains.map((terrain) => (
                <Button key={terrain.id} size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onAssignUnknownTerrain(terrain.id)}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: terrain.referenceColors?.[0] }} />{localLabel(terrain, language)}
                </Button>
              ))}
            </div>
          </div>
        )}

        {selectedVariant && !selectedVariant.unavailable && (
          draft.status === 'confirmed' ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200"><ShieldCheck className="h-4 w-4" />{text.confirmed}</p>
              <p className="mt-1 text-xs text-muted-foreground">{text.confirmedHelp}</p>
            </div>
          ) : (
            <Button className="w-full" onClick={onConfirm}><Footprints />{text.confirm}</Button>
          )
        )}

        {(draft.startHexKey || draft.destinationHexKey) && <Button variant="ghost" className="w-full" onClick={onReset}><RotateCcw />{text.reset}</Button>}
      </CardContent>
    </Card>
  );
}

export default TravelRoutePanel;
